<template>
  <Transition name="slide-down" @after-leave="handleAfterLeave">
    <div v-if="show" class="video-top-pop">
      <div ref="popContent" class="pop-content">
        <!-- 用户头像 -->
        <div class="avatar-wrapper">
          <img :src="targetMember?.avatar" alt="avatar" class="avatar" />
        </div>

        <!-- 用户信息 -->
        <div class="pop-content-info">
          <div class="user-info">
            <div class="user-name">{{ targetMember?.nickname }}</div>
            <div v-if="targetMember?.nation_flag && targetMember?.nation" class="user-nation">
              ({{ targetMember?.nation_flag }}{{ targetMember?.nation }})
            </div>
          </div>
          <p class="call-text">{{ $t('videoCall.invite.text') }}</p>
        </div>

        <!-- 操作按钮 -->
        <div class="actions">
          <!-- 接听按钮 -->
          <button class="action-btn answer-btn" :disabled="isProcessing" @click.stop="handleAnswer">
            <img src="@/assets/images/icons/video-call3.svg" alt="answer" class="btn-icon" />
            <!-- Free Badge -->
            <div v-if="videoCostInfo?.showFree && videoCostInfo?.freeIcon" class="free-badge">
              <img :src="videoCostInfo.freeIcon" alt="Free" class="free-icon" />
            </div>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { useTemplateRef, watch, computed, ref } from 'vue'
import { useCountdown, usePointerSwipe } from '@vueuse/core'
import { useVideoCall } from '@/composables/useVideoCall'
import { useUserStore } from '@/stores/user'
import { useVideoCallStore } from '@/stores/videoCall'
import { LIVE_ROOM_COME_FROM } from '@/common/constants'
import { matchPopReport } from '@/api/video'
import { getAnchorStatus } from '@/api/user'
import { getVideoCostInfo } from '@/utils'

const userStore = useUserStore()
const videoCallStore = useVideoCallStore()

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  duration: {
    type: Number,
    default: 0.3
  },
  autoClose: {
    type: Number,
    default: 0 // 自动关闭时间（毫秒），0表示不自动关闭
  },
  // 目标用户信息（包含显示信息和通话信息）
  targetMember: {
    type: Object,
    default: null,
    // targetMember: {
    //   id, member_id,
    //   avatar, nickname, nation_flag, nation,
    // }
  },
  comeFrom: {
    type: Number,
    default: LIVE_ROOM_COME_FROM.MSG_INSTEAD
  },
  popId: {
    type: Number,
    default: null
  },
})

const emit = defineEmits(['answer', 'timeout', 'closed', 'close', 'update:show'])

const scene = computed(() => props.comeFrom === LIVE_ROOM_COME_FROM.MSG_INSTEAD ? matchPopReport.SCENE.REPLY_MESSAGE : matchPopReport.SCENE.DEFAULT)

// 免费标签状态
const videoCostInfo = ref()

// 获取用户状态并计算免费标签
const fetchAnchorStatusAndCalculateFree = async () => {
  if (!props.targetMember?.id) return

  try {
    const anchorStatusData = await getAnchorStatus({
      target_ids: [props.targetMember.id],
      action: getAnchorStatus.ACTION.ONLY_CHECK, // 只查询，不上报
    }, {
      hideLoading: true,
      hideError: true,
    })

    const anchorStatus = anchorStatusData?.[0] || null

    // 使用 getVideoCostInfo 计算是否显示免费标签
    videoCostInfo.value = getVideoCostInfo({
      memberAsset: userStore.memberAsset,
      price: anchorStatus?.price,
      anchorStatus,
    })
  } catch (error) {
    console.error('[VideoTopPop] Failed to fetch anchor status:', error)
  }
}

// 初始化 useVideoCall
const { initiateVideoCall, isProcessing } = useVideoCall({
  comeFrom: props.comeFrom,
  waitingTimeout: 0,
})

// 使用 useCountdown 实现倒计时
const { start, stop } = useCountdown(0, {
  onComplete: () => {
    // 超时时上报未应答
    reportPopStatus({
      status: matchPopReport.STATUS.REFUSE,
      action: matchPopReport.ACTION.NO_ANSWER,
    })
    emit('timeout') // 超时
    emit('update:show', false)
  }
})

// 上报弹窗状态
const reportPopStatus = async (params) => {
  try {
    await matchPopReport({
      target_id: props.targetMember?.id,
      pop_id: props.popId,
      scene: scene.value,
      ...params,
    })
  } catch (error) {
    console.error('[VideoTopPop] Report pop status failed:', error)
  }
}

// 上划关闭相关
const popContentRef = useTemplateRef('popContent')
const SWIPE_THRESHOLD = 50 // 上划关闭的最小距离（像素）

// 使用 usePointerSwipe 实现滑动检测
usePointerSwipe(popContentRef, {
  threshold: SWIPE_THRESHOLD,
  disableTextSelect: true, // 禁用文本选择
  pointerTypes: ['mouse', 'touch', 'pen'], // 支持所有指针类型
  onSwipeEnd: (e, swipeDirection) => {
    // 向上滑动且距离超过阈值，关闭弹窗
    if (swipeDirection === 'up') {
      // 上划关闭时上报拒绝
      reportPopStatus({
        status: matchPopReport.STATUS.REFUSE,
        action: matchPopReport.ACTION.REFUSE,
      })
      emit('update:show', false)
      stop()
    }
  },
})

// 处理接听
const handleAnswer = async () => {
  // 检查必要参数
  if (!props.targetMember?.id || !props.targetMember?.member_id) {
    console.error('[VideoTopPop] Missing targetMember or required fields')
    return
  }
  // 上报同意
  reportPopStatus({
    status: matchPopReport.STATUS.REFUSE,
    action: matchPopReport.ACTION.AGREE,
  })

  // 发起视频通话，joinChannel 成功后再关闭弹窗
  await initiateVideoCall({
    targetId: props.targetMember.id,
    targetMemberId: props.targetMember.member_id,
    popId: props.popId,
  })
}

// 处理 Transition 的 after-leave 事件
const handleAfterLeave = () => emit('closed')

// 通话接通时关闭弹窗
watch(
  () => videoCallStore.isConnected,
  (isConnected) => {
    if (isConnected) {
      emit('update:show', false)
    }
  }
)

// 监听显示状态
watch(() => props.show, (newShow) => {
  if (newShow) {
    // 弹窗显示时上报曝光
    reportPopStatus({
      status: matchPopReport.STATUS.EXPOSE,
      action: matchPopReport.ACTION.DEFAULT,
    })

    // 显示时，如果设置了自动关闭时间，则启动倒计时
    if (props.autoClose > 0) {
      start(props.autoClose)
    }

    // 异步获取状态并计算免费标签
    fetchAnchorStatusAndCalculateFree()
  } else {
    emit('close')
    stop()
    // 重置免费标签状态
    videoCostInfo.value = null
  }
})
</script>

<style lang="less" scoped>
.video-top-pop {
  position: fixed;
  left: 0;
  right: 0;
  .safe-area-top(10px);
  z-index: 3000;
  display: flex;
  justify-content: center;
  align-items: center;
  user-select: none;

  .pop-content {
    width: 343px;
    height: 72px;
    position: relative;
    background: url('./assets/bg.png') no-repeat center center;
    background-size: cover;
    padding: 12px 16px;
    padding-left: 12px;
    display: flex;
    align-items: center;
    gap: 12px;
    touch-action: none; // 完全禁用浏览器默认触摸行为，让 JS 完全接管
    user-select: none; // 禁用文本选择
    -webkit-touch-callout: none; // 禁用 iOS 长按菜单

    &::after {
      content: '';
      position: absolute;
      bottom: 4px;
      left: 50%;
      transform: translateX(-50%);
      width: 23px;
      height: 2px;
      border-radius: 6px;
      background: #CE82C4;
      z-index: 1;
    }

    .avatar-wrapper {
      width: 48px;
      height: 48px;
      flex-shrink: 0;
      border-radius: 50%;
      overflow: hidden;

      .avatar {
        width: 48px;
        height: 48px;
        object-fit: cover;
      }
    }

    .pop-content-info {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 4px;

      .user-info {
        color: #2B2D42;
        font-size: 14px;
        font-weight: 500;
        display: flex;
        align-items: center;

        .user-name {
          max-width: 150px;
          .ellipsis();
        }
      }

      .call-text {
        color: #74758A;
        font-size: 12px;
        font-weight: 400;
        .ellipsis();
      }
    }

    .actions {
      display: flex;
      gap: 8px;
      flex-shrink: 0;

      .action-btn {
        width: 42px;
        height: 42px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        position: relative;

        .free-badge {
          position: absolute;
          top: -7px;
          right: -3px;

          .free-icon {
            height: 24px;
            width: 24px;
            object-fit: contain;
          }
        }

        .btn-icon {
          width: 100%;
          height: 100%;
        }
      }
    }
  }
}

// 滑入滑出动画
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-100%);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}
</style>
